import type { JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useAdminTeams } from '@/hooks/useAdmin';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { sectionForPath } from '@/lib/teams';
import { cn } from '@/lib/utils';

/**
 * Header control: which team the admin is looking at. Writes `?team=` so the
 * scope survives reloads and shared links; pages read it with useTeamScope().
 *
 * The default is "Social Media + Vyral": Influencer and Discord are left out
 * unless picked here. On a team section's pages the scope is fixed, so this
 * shows a static label instead.
 */
export function TeamScopeSwitcher(): JSX.Element | null {
  const { team, setTeam } = useTeamScope();
  const teams = useAdminTeams();
  const mainLabel = useMainScopeLabel();
  const location = useLocation();
  const section = sectionForPath(location.pathname);
  if (section) {
    return (
      <span className="hidden items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium sm:inline-flex">
        <Users className="h-3.5 w-3.5 text-muted-foreground" />
        {section.label} team
      </span>
    );
  }
  const options = (teams.data ?? []).filter((t) => t.isActive || t.code === team);
  return (
    <label className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
      <Users className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">Team</span>
      <select
        id="team-scope"
        value={team}
        onChange={(e) => setTeam(e.target.value)}
        className={cn(
          'btn-outline h-8 rounded-lg px-2 text-xs font-semibold text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        )}
      >
        <option value="">{mainLabel} (default)</option>
        {options.map((t) => (
          <option key={t.code} value={t.code}>
            {t.displayName}
          </option>
        ))}
      </select>
    </label>
  );
}
